import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import WelcomeParagraphs from 'Views/Onboarding/Pages/Survey/SurveyStep/Components/WelcomeParagraphs';
import { Box } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import fr from 'facet-rest';
import UploadSection from 'Components/UI/UploadSection';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';

const styles = theme => ({
  root: {
    width: '100%',
    margin: 'auto',
    display: 'flex',
    maxWidth: '400px',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: theme.spacing(10),
    padding: theme.spacing(2),
  },
  rootDesktop: {
    maxWidth: '600px',
  },
  content: {
    width: '100%',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(4),
  },
  subtitle: {
    fontWeight: theme.typography.fontWeightBold,
    padding: 0,
    marginBottom: theme.spacing(2),
  },
  subtitles: {
    fontWeight: theme.typography.fontWeightBold,
    padding: 0,
  },
  subtitleText: {
    textAlign: 'center',
  },
  or: {
    padding: theme.spacing(2, 0),
  },
});

const Content = ({
  classes,
}) => {
  const [files, setFiles] = useState([]);
  const {
    resource,
  } = useMetaLayoutContext();

  const isMobile = useMobileLayout();

  const uploadButtonRef = useRef(null);

  let subtitles = null;
  if (resource.attr('subtitle')) {
    subtitles = Array.isArray(resource.attr('subtitle')) ? (
      <WelcomeParagraphs
        className={classes.subtitles}
        paragraphs={resource.attr('subtitle').map(text => ({ text }))}
        classes={{ text: classes.subtitleText }}
        variant="h6"
      />
    ) : (
      <Subtitle className={classes.subtitle} text={resource.attr('subtitle')} test={'wizard-step-subtitle'} />
    );
  }

  // This is being done because the UploadFileStep does not currently have any way to get the full list of attachments
  // Instead we use the returned list of files to display them on the frontend
  // https://onekfinancial.atlassian.net/browse/IN-1787
  async function handleSuccess(res) {
    if (Array.isArray(res?.data?.data)) {
      setFiles(res.data.data.map(file => fr.resource(file)));
    }
  }

  async function handleDelete(file) {
    if (file?.meta()?.canDelete()) {
      try {
        await file?.link('self').delete();
        displaySuccess('Successfully deleted file', 5000);
      } catch (e) {
        const msg = e.response?.body?.errors ? e.response?.body?.errors : 'Failed to delete file';
        displayErrorMessage(e, msg);
      }
    }
  }

  return (
    <div className={classNames(classes.root, {
      [classes.rootDesktop]: !isMobile,
    })}
    >
      {resource.attr('title')
        ? <Title className={classes.title} text={resource.attr('title')} test={'wizard-step-title'} /> : null}
      {subtitles}
      <Box className={classes.content}>
        <UploadSection
          ref={uploadButtonRef}
          content={'Upload files'}
          documentResources={files}
          className={classes.upload}
          url={resource.relationship(fr.relationships.upload.name).link('related').url()}
          onSuccess={handleSuccess}
          onDelete={handleDelete}
          buttonTest="upload-file-button"
          chipTest="upload-file-chip"
          accept="image/*,.pdf,.docx"
        />
      </Box>
    </div>
  );
};

Content.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Content);
